import { z } from "zod";

import { serviceTopologySchema } from "../serviceTopology";

const normalizedStringSchema = z.string().trim().min(1);

function normalizeUnknownRecord(
  value: unknown,
): Record<string, unknown> | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }

  return Object.fromEntries(Object.entries(value));
}

function normalizeCompileRecordingDispatchMetadata(value: unknown): unknown {
  const record = normalizeUnknownRecord(value);
  if (!record) {
    return value;
  }

  const flow = normalizeUnknownRecord(record.flow);

  return {
    flow: flow
      ? {
          compile_recording_request_id: flow.compile_recording_request_id,
          execution_request_id: flow.execution_request_id,
          kind: flow.kind,
        }
      : undefined,
    source_control_provider_id:
      typeof record.source_control_provider_id === "string" ||
      record.source_control_provider_id === null
        ? record.source_control_provider_id
        : null,
    source_control_repository_allowlist: Array.isArray(
      record.source_control_repository_allowlist,
    )
      ? record.source_control_repository_allowlist
      : [],
    ticket_id:
      typeof record.ticket_id === "string" || record.ticket_id === null
        ? record.ticket_id
        : null,
  };
}

export const compileRecordingContextSchema = z
  .object({
    branch_name: z.string().trim().min(1).nullable(),
    execution_request_id: normalizedStringSchema,
    issue_key: normalizedStringSchema,
    qa_mandate: normalizedStringSchema,
    revision_id: normalizedStringSchema,
    selected_branches: z.record(z.string(), normalizedStringSchema),
    selected_repos: z.array(normalizedStringSchema),
    service_topology: serviceTopologySchema.nullable(),
  })
  .strict();

export const compileRecordingDispatchEnvelopeSchema = z
  .object({
    branch_name: z.string().trim().min(1).nullable(),
    compile_recording_context: compileRecordingContextSchema,
    compile_recording_request_id: normalizedStringSchema,
    execution_request_id: normalizedStringSchema,
    issue_key: normalizedStringSchema,
    qa_mandate: normalizedStringSchema,
    revision_id: normalizedStringSchema,
    selected_branches: z.record(z.string(), normalizedStringSchema),
    selected_repos: z.array(normalizedStringSchema),
    service_topology: serviceTopologySchema.nullable(),
  })
  .strict();

export type CompileRecordingDispatchEnvelope = z.infer<
  typeof compileRecordingDispatchEnvelopeSchema
>;

export const compileRecordingDispatchMetadataSchema = z.object({
  flow: z
    .object({
      compile_recording_request_id: normalizedStringSchema,
      execution_request_id: normalizedStringSchema,
      kind: z.literal("compile_recording.run.v1"),
    })
    .strict(),
  source_control_provider_id: z.string().trim().min(1).nullable(),
  source_control_repository_allowlist: z.array(normalizedStringSchema),
  ticket_id: z.string().trim().min(1).nullable(),
});

export type CompileRecordingDispatchMetadata = z.infer<
  typeof compileRecordingDispatchMetadataSchema
>;

export const compileRecordingFileChangeSchema = z
  .object({ path: normalizedStringSchema })
  .strict();

export const compileRecordingResultMetadataSchema = z
  .object({
    execution_request_id: normalizedStringSchema,
    issue_key: normalizedStringSchema,
    revision_id: normalizedStringSchema,
  })
  .strict();

export const compileRecordingArtifactSchema = z
  .object({
    artifact_path: z.string().trim().min(1).nullable(),
    artifact_url: z.string().trim().min(1).nullable(),
    session_id: z.string().trim().min(1).nullable(),
    target_base_url: z.string().trim().min(1).nullable(),
  })
  .strict();

export const compileRecordingCompletionEnvelopeSchema = z
  .object({
    docker_compose_path: z.string().trim().min(1).nullable(),
    files_changed: z.array(compileRecordingFileChangeSchema),
    metadata: compileRecordingResultMetadataSchema,
    recording: compileRecordingArtifactSchema,
    summary: z.string().trim().min(1).nullable(),
  })
  .strict();

export type CompileRecordingCompletionEnvelope = z.infer<
  typeof compileRecordingCompletionEnvelopeSchema
>;

export const compileRecordingTaskCallbackPayloadSchema = z
  .object({
    compile_recording_request_id: normalizedStringSchema.optional(),
    error: z.string().trim().min(1).nullable().optional(),
    metadata: z.preprocess(
      normalizeCompileRecordingDispatchMetadata,
      compileRecordingDispatchMetadataSchema.optional(),
    ),
    result: z.preprocess(
      (value) =>
        value && typeof value === "object" && !Array.isArray(value)
          ? Object.fromEntries(Object.entries(value))
          : value,
      compileRecordingCompletionEnvelopeSchema.optional(),
    ),
    status: normalizedStringSchema.optional(),
  })
  .loose();

export type CompileRecordingTaskCallbackPayload = z.infer<
  typeof compileRecordingTaskCallbackPayloadSchema
>;

export function parseCompileRecordingDispatchEnvelope(
  value: unknown,
): CompileRecordingDispatchEnvelope | null {
  const parsed = compileRecordingDispatchEnvelopeSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function serializeCompileRecordingDispatchEnvelope(
  value: CompileRecordingDispatchEnvelope,
): CompileRecordingDispatchEnvelope {
  return compileRecordingDispatchEnvelopeSchema.parse(value);
}

export function parseCompileRecordingDispatchMetadata(
  value: unknown,
): CompileRecordingDispatchMetadata | null {
  const parsed = compileRecordingDispatchMetadataSchema.safeParse(
    normalizeCompileRecordingDispatchMetadata(value),
  );
  return parsed.success ? parsed.data : null;
}

export function serializeCompileRecordingDispatchMetadata(
  value: CompileRecordingDispatchMetadata,
): CompileRecordingDispatchMetadata {
  return compileRecordingDispatchMetadataSchema.parse(value);
}

export function parseCompileRecordingCompletionEnvelope(
  value: unknown,
): CompileRecordingCompletionEnvelope | null {
  const parsed = compileRecordingCompletionEnvelopeSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function serializeCompileRecordingCompletionEnvelope(
  value: CompileRecordingCompletionEnvelope,
): CompileRecordingCompletionEnvelope {
  return compileRecordingCompletionEnvelopeSchema.parse(value);
}

export function parseCompileRecordingTaskCallbackPayload(
  value: unknown,
): CompileRecordingTaskCallbackPayload | null {
  const parsed = compileRecordingTaskCallbackPayloadSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function extractCompileRecordingCompletionEnvelope(
  value: unknown,
): CompileRecordingCompletionEnvelope | null {
  const direct = parseCompileRecordingCompletionEnvelope(value);
  if (direct) {
    return direct;
  }

  const callbackPayload = parseCompileRecordingTaskCallbackPayload(value);
  if (!callbackPayload) {
    return null;
  }

  return parseCompileRecordingCompletionEnvelope(callbackPayload.result);
}
